import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../firebase';

const WORKSPACES_COLLECTION = 'sharedWorkspaces';
const INVITES_COLLECTION = 'workspaceInvites';
const DATASETS_SUBCOLLECTION = 'datasets';
const COLLABORATORS_SUBCOLLECTION = 'collaborators';

export const SHARED_WORKSPACE_DATASETS = Object.freeze({
  TRANSACTIONS: 'transactions',
  DEPRECIATION: 'depreciation',
  SERVICE_PRICES: 'servicePrices',
  COST_ITEMS: 'costItems',
  EXTRA_CATEGORIES: 'extraCategories',
  GOALS: 'goals',
  VAT_SETTINGS: 'vatSettings',
  CLIENTS: 'clients',
  BUSINESS_PROFILE: 'businessProfile',
  BANK_ACCOUNTS: 'bankAccounts',
  JOURNAL_ENTRIES: 'journalEntries'
});

const DATASET_KEYS = Object.freeze(Object.values(SHARED_WORKSPACE_DATASETS));
const DATASET_KEY_SET = new Set(DATASET_KEYS);

const normalizeEmail = (email = '') => String(email || '').trim().toLowerCase();

const stripUndefinedFields = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;

  if (Array.isArray(value)) {
    return value
      .map((entry) => stripUndefinedFields(entry))
      .filter((entry) => entry !== undefined);
  }

  if (typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, entryValue]) => {
      const sanitizedValue = stripUndefinedFields(entryValue);
      if (sanitizedValue !== undefined) {
        acc[key] = sanitizedValue;
      }
      return acc;
    }, {});
  }

  return value;
};

const toMillis = (value) => {
  if (!value) return 0;

  if (typeof value?.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : 0;
  }

  if (typeof value === 'object' && value.seconds !== undefined) {
    const seconds = Number(value.seconds) || 0;
    const nanoseconds = Number(value.nanoseconds) || 0;
    return (seconds * 1000) + Math.round(nanoseconds / 1000000);
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortNewestFirst = (left, right) => {
  const leftTime = Math.max(toMillis(left?.updatedAt), toMillis(left?.acceptedAt), toMillis(left?.createdAt));
  const rightTime = Math.max(toMillis(right?.updatedAt), toMillis(right?.acceptedAt), toMillis(right?.createdAt));
  return rightTime - leftTime;
};

const mapWorkspaceSnapshot = (snapshot) => {
  if (!snapshot?.exists()) return null;
  return {
    id: snapshot.id,
    ...snapshot.data()
  };
};

const mapInviteSnapshot = (snapshot) => ({
  id: snapshot.id,
  ...snapshot.data()
});

const getWorkspaceRef = (workspaceId) => doc(db, WORKSPACES_COLLECTION, workspaceId);
const getInviteRef = (inviteId) => doc(db, INVITES_COLLECTION, inviteId);
const getDatasetRef = (workspaceId, datasetKey) =>
  doc(db, WORKSPACES_COLLECTION, workspaceId, DATASETS_SUBCOLLECTION, datasetKey);
const getCollaboratorRef = (workspaceId, collaboratorUid) =>
  doc(db, WORKSPACES_COLLECTION, workspaceId, COLLABORATORS_SUBCOLLECTION, collaboratorUid);
const canUpdateWorkspaceMetadata = (workspaceId, actor) => !actor?.uid || actor.uid === workspaceId;

const removeInviteCollaborators = async (invite) => {
  if (!invite?.workspaceId) return;

  const collaboratorRefs = new Map();
  if (invite.acceptedByUid) {
    collaboratorRefs.set(invite.acceptedByUid, getCollaboratorRef(invite.workspaceId, invite.acceptedByUid));
  }

  try {
    const snapshot = await getDocs(
      query(
        collection(db, WORKSPACES_COLLECTION, invite.workspaceId, COLLABORATORS_SUBCOLLECTION),
        where('inviteId', '==', invite.id)
      )
    );

    snapshot.docs.forEach((collaboratorSnapshot) => {
      if (collaboratorSnapshot?.ref) {
        collaboratorRefs.set(collaboratorSnapshot.id || collaboratorSnapshot.ref.id, collaboratorSnapshot.ref);
      }
    });
  } catch (error) {
    console.warn('Failed to query collaborator memberships for invite cleanup:', error);
  }

  await Promise.all([...collaboratorRefs.values()].map((collaboratorRef) => deleteDoc(collaboratorRef)));
};

export const getOwnWorkspace = async (ownerId) => {
  if (!ownerId) return null;
  const snapshot = await getDoc(getWorkspaceRef(ownerId));
  const workspace = mapWorkspaceSnapshot(snapshot);
  if (!workspace) return null;

  return {
    ...workspace,
    ownerId: workspace.ownerId || workspace.id,
    accessRole: 'owner'
  };
};

export const listOwnerWorkspaceInvites = async (ownerId) => {
  if (!ownerId) return [];

  const snapshot = await getDocs(
    query(collection(db, INVITES_COLLECTION), where('ownerId', '==', ownerId))
  );

  return snapshot.docs
    .map(mapInviteSnapshot)
    .sort(sortNewestFirst);
};

export const listWorkspaceInvitesForEmail = async (email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return [];

  const snapshot = await getDocs(
    query(collection(db, INVITES_COLLECTION), where('inviteeEmail', '==', normalizedEmail))
  );

  return snapshot.docs
    .map(mapInviteSnapshot)
    .sort(sortNewestFirst);
};

export const getCollaborationStateForUser = async (user) => {
  if (!user?.uid) {
    return {
      ownWorkspace: null,
      sentInvites: [],
      pendingInvites: [],
      acceptedWorkspaces: []
    };
  }

  const [ownWorkspace, sentInvites, emailInvites] = await Promise.all([
    getOwnWorkspace(user.uid),
    listOwnerWorkspaceInvites(user.uid),
    listWorkspaceInvitesForEmail(user.email)
  ]);

  const pendingInvites = emailInvites.filter((invite) => invite.status === 'pending');

  const acceptedWorkspaceMap = new Map();
  emailInvites
    .filter((invite) => invite.status === 'accepted' && invite.workspaceId)
    .forEach((invite) => {
      const workspaceId = invite.workspaceId;
      const current = acceptedWorkspaceMap.get(workspaceId);
      if (!current || sortNewestFirst(invite, current) < 0) {
        acceptedWorkspaceMap.set(workspaceId, {
          id: workspaceId,
          workspaceId,
          ownerId: invite.ownerId || workspaceId,
          ownerEmail: invite.ownerEmail || '',
          ownerDisplayName: invite.ownerDisplayName || invite.ownerEmail || 'Shared workspace',
          inviteId: invite.id,
          accessRole: invite.permissions?.includes('edit') ? 'editor' : 'viewer',
          status: invite.status,
          acceptedAt: invite.acceptedAt || null,
          updatedAt: invite.updatedAt || invite.acceptedAt || invite.createdAt || null
        });
      }
    });

  return {
    ownWorkspace,
    sentInvites,
    pendingInvites: pendingInvites.sort(sortNewestFirst),
    acceptedWorkspaces: [...acceptedWorkspaceMap.values()].sort(sortNewestFirst)
  };
};

export const syncAcceptedInviteMemberships = async (user) => {
  if (!user?.uid || !user?.email) return;

  const invites = await listWorkspaceInvitesForEmail(user.email);
  const acceptedInvites = invites.filter((invite) =>
    invite.status === 'accepted' &&
    invite.workspaceId &&
    (invite.acceptedByUid === user.uid || !invite.acceptedByUid)
  );
  const acceptedWorkspaceIds = new Set(acceptedInvites.map((invite) => invite.workspaceId));
  const inactiveInvites = invites.filter((invite) =>
    invite.status !== 'accepted' &&
    invite.workspaceId &&
    !acceptedWorkspaceIds.has(invite.workspaceId) &&
    (invite.acceptedByUid === user.uid || !invite.acceptedByUid)
  );

  await Promise.all(
    acceptedInvites.map((invite) =>
      setDoc(
        getCollaboratorRef(invite.workspaceId, user.uid),
        {
          workspaceId: invite.workspaceId,
          ownerId: invite.ownerId || invite.workspaceId,
          inviteId: invite.id,
          collaboratorUid: user.uid,
          collaboratorEmail: normalizeEmail(user.email),
          collaboratorDisplayName: user.displayName || user.email || 'Accountant',
          permissions: Array.isArray(invite.permissions) ? invite.permissions : ['view', 'edit'],
          acceptedAt: invite.acceptedAt || serverTimestamp(),
          updatedAt: serverTimestamp()
        },
        { merge: true }
      )
    )
  );

  await Promise.all(
    inactiveInvites.map((invite) => deleteDoc(getCollaboratorRef(invite.workspaceId, user.uid)))
  );
};

export const ensureSharedWorkspace = async ({ owner, datasets = null } = {}) => {
  if (!owner?.uid) {
    throw new Error('Owner account is required to create a shared workspace.');
  }

  const workspaceId = owner.uid;
  const workspaceRef = getWorkspaceRef(workspaceId);
  const ownerEmail = normalizeEmail(owner.email);
  const ownerDisplayName = owner.displayName || owner.name || owner.email || 'Workspace owner';
  const existingWorkspaceSnapshot = await getDoc(workspaceRef);

  await setDoc(
    workspaceRef,
    {
      ownerId: owner.uid,
      ownerEmail,
      ownerDisplayName,
      collaborationEnabled: true,
      updatedAt: serverTimestamp(),
      ...(existingWorkspaceSnapshot.exists() ? {} : { createdAt: serverTimestamp() })
    },
    { merge: true }
  );

  if (datasets && typeof datasets === 'object') {
    await syncWorkspaceDatasets(workspaceId, datasets, owner);
  }

  const workspaceSnapshot = await getDoc(workspaceRef);
  return mapWorkspaceSnapshot(workspaceSnapshot);
};

export const createAccountantInvite = async ({ owner, accountantEmail, datasets = null } = {}) => {
  const normalizedAccountantEmail = normalizeEmail(accountantEmail);
  if (!owner?.uid || !owner?.email) {
    throw new Error('You must be signed in to invite an accountant.');
  }
  if (!normalizedAccountantEmail) {
    throw new Error('Enter the accountant email address you want to invite.');
  }
  if (normalizedAccountantEmail === normalizeEmail(owner.email)) {
    throw new Error('Use a different email address for the accountant invitation.');
  }

  const workspace = await ensureSharedWorkspace({ owner, datasets });
  const existingInvites = await listOwnerWorkspaceInvites(owner.uid);
  const matchingInvite = existingInvites.find(
    (invite) => invite.workspaceId === workspace.id && invite.inviteeEmail === normalizedAccountantEmail
  );

  if (matchingInvite?.status === 'accepted') {
    return {
      workspace,
      invite: matchingInvite,
      alreadyExists: true
    };
  }

  if (matchingInvite) {
    await updateDoc(getInviteRef(matchingInvite.id), {
      status: 'pending',
      updatedAt: serverTimestamp(),
      acceptedAt: null,
      acceptedByUid: null,
      acceptedByEmail: null,
      acceptedDisplayName: null,
      revokedAt: null
    });

    const refreshedInvite = (await listOwnerWorkspaceInvites(owner.uid)).find(
      (invite) => invite.id === matchingInvite.id
    );

    return {
      workspace,
      invite: refreshedInvite || matchingInvite,
      alreadyExists: false
    };
  }

  const inviteRef = await addDoc(collection(db, INVITES_COLLECTION), {
    workspaceId: workspace.id,
    ownerId: owner.uid,
    ownerEmail: normalizeEmail(owner.email),
    ownerDisplayName: owner.displayName || owner.email || 'Workspace owner',
    inviteeEmail: normalizedAccountantEmail,
    permissions: ['view', 'edit'],
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  const inviteSnapshot = await getDoc(inviteRef);
  return {
    workspace,
    invite: mapInviteSnapshot(inviteSnapshot),
    alreadyExists: false
  };
};

export const acceptAccountantInvite = async ({ inviteId, user } = {}) => {
  if (!inviteId) {
    throw new Error('Invite ID is required.');
  }
  if (!user?.uid || !user?.email) {
    throw new Error('You must be signed in to accept an invitation.');
  }

  const inviteRef = getInviteRef(inviteId);
  const inviteSnapshot = await getDoc(inviteRef);
  if (!inviteSnapshot.exists()) {
    throw new Error('Invite not found.');
  }

  const invite = mapInviteSnapshot(inviteSnapshot);
  if (invite.status === 'revoked') {
    throw new Error('This invite has been revoked.');
  }

  const normalizedUserEmail = normalizeEmail(user.email);
  if (invite.inviteeEmail !== normalizedUserEmail) {
    throw new Error('This invite was sent to a different email address.');
  }

  await updateDoc(inviteRef, {
    status: 'accepted',
    updatedAt: serverTimestamp(),
    acceptedAt: serverTimestamp(),
    acceptedByUid: user.uid,
    acceptedByEmail: normalizedUserEmail,
    acceptedDisplayName: user.displayName || user.email || 'Accountant'
  });

  await setDoc(
    getCollaboratorRef(invite.workspaceId, user.uid),
    {
      workspaceId: invite.workspaceId,
      ownerId: invite.ownerId || invite.workspaceId,
      inviteId: invite.id,
      collaboratorUid: user.uid,
      collaboratorEmail: normalizedUserEmail,
      collaboratorDisplayName: user.displayName || user.email || 'Accountant',
      permissions: Array.isArray(invite.permissions) ? invite.permissions : ['view', 'edit'],
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  const refreshedInviteSnapshot = await getDoc(inviteRef);
  const refreshedInvite = mapInviteSnapshot(refreshedInviteSnapshot);

  return {
    invite: refreshedInvite,
    workspace: {
      id: refreshedInvite.workspaceId,
      workspaceId: refreshedInvite.workspaceId,
      ownerId: refreshedInvite.ownerId || refreshedInvite.workspaceId,
      ownerEmail: refreshedInvite.ownerEmail || '',
      ownerDisplayName: refreshedInvite.ownerDisplayName || refreshedInvite.ownerEmail || 'Shared workspace',
      inviteId: refreshedInvite.id,
      accessRole: refreshedInvite.permissions?.includes('edit') ? 'editor' : 'viewer',
      status: refreshedInvite.status,
      acceptedAt: refreshedInvite.acceptedAt || null,
      updatedAt: refreshedInvite.updatedAt || refreshedInvite.acceptedAt || refreshedInvite.createdAt || null
    }
  };
};

export const revokeAccountantInvite = async (inviteId) => {
  if (!inviteId) {
    throw new Error('Invite ID is required.');
  }

  const inviteSnapshot = await getDoc(getInviteRef(inviteId));
  const invite = inviteSnapshot.exists() ? mapInviteSnapshot(inviteSnapshot) : null;

  await updateDoc(getInviteRef(inviteId), {
    status: 'revoked',
    updatedAt: serverTimestamp(),
    revokedAt: serverTimestamp()
  });

  if (invite?.workspaceId) {
    try {
      await removeInviteCollaborators(invite);
    } catch (error) {
      console.warn('Failed to remove collaborator membership after revoke:', error);
    }
  }
};

export const deleteAccountantInvite = async (inviteId) => {
  if (!inviteId) {
    throw new Error('Invite ID is required.');
  }

  const inviteSnapshot = await getDoc(getInviteRef(inviteId));
  const invite = inviteSnapshot.exists() ? mapInviteSnapshot(inviteSnapshot) : null;

  if (invite?.workspaceId) {
    try {
      await removeInviteCollaborators(invite);
    } catch (error) {
      console.warn('Failed to remove collaborator membership before invite deletion:', error);
    }
  }

  await deleteDoc(getInviteRef(inviteId));
};

export const loadWorkspaceDatasets = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required.');
  }

  const datasetEntries = await Promise.all(
    DATASET_KEYS.map(async (datasetKey) => {
      const snapshot = await getDoc(getDatasetRef(workspaceId, datasetKey));
      if (!snapshot.exists()) {
        return [datasetKey, undefined];
      }

      const payload = snapshot.data();
      return [datasetKey, payload?.data];
    })
  );

  return Object.fromEntries(datasetEntries);
};

export const saveWorkspaceDataset = async (workspaceId, datasetKey, data, actor = null) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required.');
  }
  if (!DATASET_KEY_SET.has(datasetKey)) {
    throw new Error(`Unsupported workspace dataset: ${datasetKey}`);
  }

  const sanitizedData = stripUndefinedFields(data);
  await setDoc(
    getDatasetRef(workspaceId, datasetKey),
    {
      key: datasetKey,
      data: sanitizedData,
      updatedAt: serverTimestamp(),
      updatedByUid: actor?.uid || null,
      updatedByEmail: normalizeEmail(actor?.email || ''),
      updatedByDisplayName: actor?.displayName || actor?.email || null
    },
    { merge: true }
  );

  if (!canUpdateWorkspaceMetadata(workspaceId, actor)) {
    return;
  }

  await setDoc(
    getWorkspaceRef(workspaceId),
    {
      updatedAt: serverTimestamp(),
      lastUpdatedByUid: actor?.uid || null,
      lastUpdatedByEmail: normalizeEmail(actor?.email || ''),
      lastUpdatedByDisplayName: actor?.displayName || actor?.email || null
    },
    { merge: true }
  );
};

export const syncWorkspaceDatasets = async (workspaceId, datasets = {}, actor = null) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required.');
  }

  const datasetEntries = Object.entries(datasets).filter(([datasetKey]) => DATASET_KEY_SET.has(datasetKey));
  await Promise.all(
    datasetEntries.map(([datasetKey, value]) => saveWorkspaceDataset(workspaceId, datasetKey, value, actor))
  );
};
